import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import SessionsTableEditRow, { validate } from './SessionsTableEditRow';

import { apiClient } from 'Helpers/network';

import BulkDeleteControls from 'CommonComponents/BulkDeleteControls';
import Button from 'CommonComponents/Button';
import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import { useBulkDelete } from 'Context/BulkDeleteContext';

import useSort from 'Hooks/useSort';
import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

const SORT_TYPE_EVENT = 'event';
const SORT_TYPE_VENUE = 'venue';
const SORT_TYPE_DATE = 'date';

/**
 * Build an initial draft object from a session row as returned by the server.
 * The date is stored as "d-m-Y" string (what the PATCH endpoint expects).
 */
const buildDraft = (session) => ({
    venue_id: session.venue?.id ?? null,
    schedule_type_id: session.schedule_type?.id ?? null,
    date: session.start ? moment(session.start).format('DD-MM-YYYY') : null,
    start_time: session.start ? moment(session.start).format('HH:mm') : null,
    end_time: session.end ? moment(session.end).format('HH:mm') : null,
});

const SessionsTable = (props) => {
    const [sessions, setSessions] = useState([]);
    const [request, setRequest] = useState(false);
    const [venues, setVenues] = useState([]);

    /**
     * editStates: { [sessionId]: { editing: bool, draft: object, original: object } }
     */
    const [editStates, setEditStates] = useState({});
    const [rowErrors, setRowErrors] = useState({});
    const [isConfirmingAll, setIsConfirmingAll] = useState(false);
    const [savingIds, setSavingIds] = useState(new Set());

    const sort = useSort();
    const toast = useToast();
    const bulkDelete = useBulkDelete();

    const hasAnyEditing = useMemo(() => Object.values(editStates).some((s) => s.editing), [editStates]);
    const hasAnyDirty = useMemo(() => Object.values(editStates).some((s) => JSON.stringify(s.draft) !== JSON.stringify(s.original)), [editStates]);

    // Memoised so the Echo listener (and other effects) always refetch with the
    // user's CURRENT sort rather than a stale closure captured on first render.
    const fetchSessions = useCallback(() => {
        axios
            .get(props.fetchSchedulesUrl, {
                params: {
                    sort_by: sort.type,
                    sort_dir: sort.dir,
                },
            })
            .then(({ data }) => {
                setSessions(data);
            })
            .catch((error) => {
                console.error('Error fetching sessions:', error);
            });
    }, [props.fetchSchedulesUrl, sort.type, sort.dir]);

    // Keep the latest fetchSessions in a ref so the Echo listener can call it
    // without re-subscribing the Pusher channel on every sort change.
    const fetchSessionsRef = useRef(fetchSessions);
    useEffect(() => {
        fetchSessionsRef.current = fetchSessions;
    });

    // Warn before navigating away with unsaved edits
    useEffect(() => {
        if (!hasAnyEditing) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasAnyEditing]);

    useEffect(() => {
        fetchSessions();
    }, []);

    useEffect(() => {
        if (sort.type && sort.dir) {
            fetchSessions();
        }
    }, [sort.type, sort.dir, fetchSessions]);

    // Listen for the queued bulk-delete job completing and refetch the table.
    useEffect(() => {
        if (!window.Echo || !props.eventId || !props.budgetId) return;

        const channelName = `event.${props.eventId}.budget.${props.budgetId}.sessions`;
        const channel = window.Echo.private(channelName);

        channel.listen('.sessions.bulk-deleted', (e) => {
            toast.success($t('{{count}} sessions deleted', { count: e.deletedCount }));
            fetchSessionsRef.current();
        });

        return () => window.Echo.leave(channelName);
    }, [props.eventId, props.budgetId]);

    // Fetch venues once for the inline-edit dropdowns
    useEffect(() => {
        if (!props.fetchVenuesUrl) return;
        axios
            .get(props.fetchVenuesUrl)
            .then(({ data }) => setVenues(data))
            .catch((err) => console.error('Error fetching venues:', err));
    }, [props.fetchVenuesUrl]);

    // ─── Edit-state helpers ────────────────────────────────────────────────────

    const enterEditMode = (session) => {
        const dirtyIds = Object.entries(editStates)
            .filter(([id, s]) => s.editing && isDirty(Number(id)))
            .map(([id]) => id);

        if (dirtyIds.length > 0) {
            const confirmed = window.confirm($t('You have unsaved changes in other rows. Do you want to continue editing multiple rows?'));
            if (!confirmed) return;
        }

        const draft = buildDraft(session);
        setEditStates((prev) => ({
            ...prev,
            [session.id]: { editing: true, draft, original: { ...draft } },
        }));
    };

    const exitEditMode = (sessionId) => {
        setEditStates((prev) => {
            const next = { ...prev };
            delete next[sessionId];
            return next;
        });
        setRowErrors((prev) => {
            const next = { ...prev };
            delete next[sessionId];
            return next;
        });
    };

    const updateDraft = (sessionId, field, value) => {
        setEditStates((prev) => ({
            ...prev,
            [sessionId]: {
                ...prev[sessionId],
                draft: { ...prev[sessionId].draft, [field]: value },
            },
        }));
    };

    const isDirty = (sessionId) => {
        const state = editStates[sessionId];
        if (!state) return false;
        return JSON.stringify(state.draft) !== JSON.stringify(state.original);
    };

    // ─── PATCH helpers ─────────────────────────────────────────────────────────

    /**
     * Build the update URL by substituting the schedule ID into the base URL template.
     */
    const updateUrl = (sessionId) => props.updateScheduleBaseUrl.replace('__ID__', sessionId);

    /**
     * Send a PATCH for a single session. Returns true on success, false on failure.
     * Does NOT call fetchSessions — callers are responsible for that.
     */
    const patchSession = async (session) => {
        const state = editStates[session.id];
        if (!state) return false;

        setSavingIds((prev) => new Set([...prev, session.id]));

        const payload = {
            ...state.draft,
            sport_id: session.sport?.id,
            name: session.name,
        };

        try {
            const response = await axios.patch(updateUrl(session.id), payload);
            exitEditMode(session.id);

            if (response.data?.url) {
                toast.success(response.data.message || $t('Approval request submitted'));
                window.location.href = response.data.url;
                return true;
            }

            toast.success($t('Session updated successfully'));
            return true;
        } catch (err) {
            console.error('Error updating session:', err);
            toast.error($t('Failed to save session, please try again'));
            return false;
        } finally {
            setSavingIds((prev) => {
                const next = new Set(prev);
                next.delete(session.id);
                return next;
            });
        }
    };

    const handleConfirmAll = async () => {
        const editingSessions = sessions.filter((s) => editStates[s.id]?.editing && isDirty(s.id));

        const newErrors = {};
        for (const session of editingSessions) {
            const errs = validate(editStates[session.id].draft);
            if (Object.keys(errs).length > 0) newErrors[session.id] = errs;
        }

        if (Object.keys(newErrors).length > 0) {
            setRowErrors((prev) => ({ ...prev, ...newErrors }));
            return;
        }

        setIsConfirmingAll(true);
        for (const session of editingSessions) {
            await patchSession(session);
        }
        setIsConfirmingAll(false);
        setEditStates({});
        setRowErrors({});

        fetchSessions();
    };

    // ─── Bulk delete ───────────────────────────────────────────────────────────

    const handleDeleteMany = async () => {
        const result = await bulkDelete.bulkDelete(
            (selectedIds) => {
                return apiClient.post(props.bulkDeleteUrl, { all: bulkDelete.isAllMode, ids: selectedIds });
            },
            {
                confirmTitle: $t('Delete Sessions'),
                confirmMessage: bulkDelete.isAllMode
                    ? $t('Are you sure you want to delete all sessions?')
                    : $t('Are you sure you want to delete the selected sessions?'),
            },
        );

        if (!result.success) {
            toast.error(result.error);
            return;
        }

        toast.success(result?.data?.message || $t('Sessions successfully deleted'));
        fetchSessions();
    };

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="table__card">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
                <h4 className="table__header">{$t('Sessions')}</h4>
                <div className="tw-flex tw-justify-end tw-items-center tw-gap-1.5">
                    {hasAnyDirty && (
                        <Button variant="primary" onClick={handleConfirmAll} loading={isConfirmingAll} disabled={isConfirmingAll}>
                            {$t('Confirm All')}
                        </Button>
                    )}
                    <BulkDeleteControls
                        onDeleteAll={handleDeleteMany}
                        onDeleteMany={handleDeleteMany}
                        deleteManyDisabled={request}
                        disabled={!sessions?.length}
                        loading={bulkDelete.isDeleting}
                    />
                    {!!props.canScheduleCreate && (
                        <Button as="a" variant="primary" href={props.schedulesImportUrl}>
                            {$t('Import sessions from file')}
                        </Button>
                    )}
                    <Button variant="primary" as="a" href={props.exportSchedulesUrl}>
                        {$t('Download to Excel')}
                    </Button>
                </div>
            </div>

            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th className={classNames('tw-w-8', { 'tw-hidden': bulkDelete.isAllMode })}>
                                {!!bulkDelete.selectedCount && (
                                    <div className="tw-rounded-full tw-h-4 tw-w-4 tw-flex tw-items-center tw-justify-center tw-text-2xs tw-text-white tw-bg-red-75">
                                        {bulkDelete.selectedCount}
                                    </div>
                                )}
                            </th>
                            <th className="tw-cursor-pointer" onClick={() => sort.toggleSort(SORT_TYPE_EVENT)}>
                                <TableSortHeaderCell asc={sort.isAsc(SORT_TYPE_EVENT)} desc={sort.isDesc(SORT_TYPE_EVENT)}>
                                    {$t('Event')}
                                </TableSortHeaderCell>
                            </th>
                            <th className="tw-cursor-pointer" onClick={() => sort.toggleSort(SORT_TYPE_VENUE)}>
                                <TableSortHeaderCell asc={sort.isAsc(SORT_TYPE_VENUE)} desc={sort.isDesc(SORT_TYPE_VENUE)}>
                                    {$t('Venue')}
                                </TableSortHeaderCell>
                            </th>
                            <th className="tw-cursor-pointer" onClick={() => sort.toggleSort(SORT_TYPE_DATE)}>
                                <TableSortHeaderCell asc={sort.isAsc(SORT_TYPE_DATE)} desc={sort.isDesc(SORT_TYPE_DATE)}>
                                    {$t('Date')}
                                </TableSortHeaderCell>
                            </th>
                            <th>{$t('Start Time')}</th>
                            <th>{$t('End Time')}</th>
                            <th>{$t('Session Type')}</th>
                            {!!props.canScheduleUpdate && <th>{$t('Actions')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {sessions.map((session) => {
                            const editState = editStates[session.id];
                            if (editState?.editing) {
                                return (
                                    <SessionsTableEditRow
                                        key={session.id}
                                        session={session}
                                        draft={editState.draft}
                                        venues={venues}
                                        scheduleTypes={props.scheduleTypes || []}
                                        isDirty={isDirty(session.id)}
                                        isSaving={savingIds.has(session.id)}
                                        onConfirm={() => patchSession(session).then((ok) => ok && fetchSessions())}
                                        onCancel={() => exitEditMode(session.id)}
                                        onChange={(field, value) => updateDraft(session.id, field, value)}
                                        errors={rowErrors[session.id] || {}}
                                        onErrorsChange={(errs) => setRowErrors((prev) => ({ ...prev, [session.id]: errs }))}
                                        bulkDeleteMode={bulkDelete.isAllMode}
                                        showActionsColumn={!!props.canScheduleUpdate}
                                        eventStartDate={props.eventStartDate}
                                        eventEndDate={props.eventEndDate}
                                    />
                                );
                            }
                            return (
                                <SessionsTableRow
                                    key={session.id}
                                    session={session}
                                    canEdit={!!props.canScheduleUpdate}
                                    onEdit={() => enterEditMode(session)}
                                    showActionsColumn={!!props.canScheduleUpdate}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const SessionsTableRow = ({ session, canEdit, onEdit, showActionsColumn }) => {
    const [showDetails, setShowDetails] = useState(false);

    const bulkDelete = useBulkDelete();

    const expandable = useMemo(() => session.events.length > 0, [session.events]);

    return (
        <Fragment>
            <tr className={expandable ? 'tw-cursor-pointer' : ''} onClick={() => expandable && setShowDetails((state) => !state)}>
                {/* Hidden checkbox cell */}
                <td className={classNames({ 'tw-hidden': bulkDelete.isAllMode })}>
                    <input
                        type="checkbox"
                        className="input-checkbox"
                        checked={bulkDelete.isSelected(session.id)}
                        onChange={() => bulkDelete.toggleItem(session.id)}
                    />
                </td>

                {!expandable ? (
                    <td>{session.sport.name}</td>
                ) : (
                    <td>
                        <div className="tw-flex tw-items-center tw-gap-x-1.5">
                            <div>{session.sport.name}</div>
                            <div>{showDetails ? <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" /> : <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" />}</div>
                        </div>
                    </td>
                )}
                <td>{session.venue?.name}</td>
                <td className="inbud-date-cell">{moment(session.start).format('DD/MM/YYYY')}</td>
                <td className="inbud-date-cell">{moment(session.start).format('HH:mm')}</td>
                <td className="inbud-date-cell">{moment(session.end).format('HH:mm')}</td>
                <td>{session.schedule_type.name}</td>
                {showActionsColumn && (
                    <td>
                        {canEdit && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit();
                                }}
                                className="tw-text-xs tw-font-medium tw-text-blue-600 tw-underline"
                            >
                                {$t('Edit')}
                            </button>
                        )}
                    </td>
                )}
            </tr>
            {expandable && showDetails && (
                <tr className="!tw-bg-white">
                    <td colSpan={showActionsColumn ? 8 : 7}>
                        <SessionEventsTable events={session.events} />
                    </td>
                </tr>
            )}
        </Fragment>
    );
};

const SessionEventsTable = ({ events }) => {
    return (
        <div>
            <h5 className="table__header">{$t('Session Details')}:</h5>
            <div className="inbud-table_wrapper">
                <table className="inbud-table tw-table-fixed">
                    <thead>
                        <tr>
                            <th className="tw-w-1/2">{$t('Event Name')}</th>
                            <th className="tw-w-1/2">{$t('Details')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map((event) => (
                            <tr key={event.id}>
                                <td>{event.name}</td>
                                <td>{event.details}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SessionsTable;
